/**
 * Profile API functions
 * API calls for user profile management
 */

import { apiClient } from '@/core/api/client';

export type UserProfile = {
  id: string;
  email: string;
  kindeId: string;
  firstName: string;
  lastName?: string;
  role: string;
  profilePhoto?: string | null;
  onboardingCompleted?: boolean;
  profileCompleted?: boolean;
  /** Only on the current user's profile: what still blocks completeness. */
  missingProfileFields?: { key: string; label: string; selfEditable: boolean }[];
  accountStatus?: string;
  vettingStatus?: string;
};

export type UpdateProfileRequest = {
  firstName?: string;
  lastName?: string;
  email?: string;
  profilePhoto?: string | null;
};

export type NotificationPreferencesPayload = {
  emailMessages: boolean;
};

/**
 * Get current user profile
 */
export const getUserProfile = async (): Promise<UserProfile> => {
  const response = await apiClient.get<UserProfile>('/users/profile/me');
  return response.data;
};

/**
 * Update current user profile
 * Triggers profileCompleted check and token refresh event if profile becomes complete (FR-125)
 */
export const updateUserProfile = async (
  data: UpdateProfileRequest
): Promise<UserProfile> => {
  const response = await apiClient.patch<UserProfile>('/users/profile/me', data);
  return response.data;
};

export const getNotificationPreferences = async (): Promise<NotificationPreferencesPayload> => {
  const response = await apiClient.get<NotificationPreferencesPayload>(
    '/users/profile/me/notification-preferences'
  );
  return response.data;
};

export const updateNotificationPreferences = async (
  data: NotificationPreferencesPayload
): Promise<NotificationPreferencesPayload> => {
  const response = await apiClient.patch<NotificationPreferencesPayload>(
    '/users/profile/me/notification-preferences',
    data
  );
  return response.data;
};

/**
 * Upload profile photo via backend
 * Backend handles S3 upload and updates user profile in one operation
 * 
 * Process:
 * 1. Send file to backend as multipart/form-data
 * 2. Backend uploads to S3 and updates user profile
 * 3. Return updated user profile with new photo URL
 */
export const uploadProfilePhoto = async (file: File): Promise<UserProfile> => {
  // Create FormData to send file as multipart/form-data
  const formData = new FormData();
  formData.append('file', file);

  // Send file to backend - backend handles S3 upload and profile update
  const response = await apiClient.post<UserProfile>(
    '/users/profile/me/photo',
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );

  return response.data;
};

